import React from 'react';
import styles from './RelatedBlock.module.scss';
import { ComponentProps } from 'lib/component-props';
import { useI18n } from 'next-localization';
import { CapabilityCard } from 'model/Article';
import NextLink from 'next/link';
import NextImage from 'next/image';

type RelatedBlockProps = {
  rendering: ComponentProps['rendering'];
  title?: string;
  items: CapabilityCard[];
  sidebar?: boolean;
  helpArticle?: boolean;
  className?: string;
};

const RelatedBlock: React.FC<RelatedBlockProps> = ({
  rendering,
  title = '',
  items = [],
  sidebar = true,
  helpArticle = false,
  className,
}) => {
  const { t } = useI18n();
  const getSubtitle = (name: string) => {
    return helpArticle ? t(`relatedHelpPlatformTitle`).replace('{token.Platform}', name) : name;
  };

  if (items.length === 0 && !rendering) return null;

  return (
    <div id={rendering?.uid} data-e2e-component-uid={rendering.uid} className={className}>
      {sidebar && <h5 className="mb-3">{title}</h5>}

      <div className={sidebar ? 'd-flex flex-column gap-3' : ''}>
        {items.map((item) => (
          <NextLink
            key={item.id}
            href={item.link}
            className={`${styles.card} ${sidebar ? styles['card--sidebar'] : ''}`}
          >
            {sidebar && (
              <div className={`position-relative ${styles.card__image}`}>
                <NextImage
                  src={item.image.url}
                  alt={item.image.alt}
                  fill
                  style={{ objectFit: 'cover' }}
                  sizes="
                        (max-width: 991px) 80vw,
                        270px
                    "
                />
              </div>
            )}
            <div className={`p-3 background-digital-deep-grey ${styles.subtitle}`}>
              {item.name && (
                <div className={sidebar ? 'footnote' : 'section-header-small'}>
                  {getSubtitle(item.name)}
                </div>
              )}
              {item.title && (
                <div className="mt-1 d-flex justify-content-between align-items-stretch gap-2">
                  <div className={styles.card__link__wrapper}>
                    <div className={styles.card__link}>{item.title}</div>
                  </div>

                  {sidebar && (
                    <div className={styles.card__icon__wrapper}>
                      <svg
                        viewBox="0 0 36 36"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className={styles.card__icon}
                        aria-hidden="true"
                      >
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M20.5843 12.2386L25.7617 17.4239C26.0794 17.7421 26.0794 18.2579 25.7617 18.5761L20.5843 23.7614C20.2665 24.0795 19.7514 24.0795 19.4337 23.7614C19.116 23.4432 19.116 22.9273 19.4337 22.6091L23.2223 18.8148H10.8148C10.3648 18.8148 10 18.45 10 18C10 17.55 10.3648 17.1852 10.8148 17.1852H23.2223L19.4337 13.3909C19.116 13.0727 19.116 12.5568 19.4337 12.2386C19.7514 11.9205 20.2665 11.9205 20.5843 12.2386Z"
                          fill="currentColor"
                        />
                      </svg>
                    </div>
                  )}
                </div>
              )}
            </div>
          </NextLink>
        ))}
      </div>
    </div>
  );
};

export default RelatedBlock;
